import { toast } from "react-toastify";
import { handleError } from "../../../utils/helpers";
import {
  BaseApiResponse,
  GetAllAdminResponse,
  GetAllHolidayResponse,
  QueryWithStatus,
  QueryWithStatusYear,
} from "../../../utils/types";
import { adminBaseApi } from "../../api";
import {
  AddHolidayPayload,
  AddHolidayResponse,
  ChangeAdminRoleResponse,
  PayoutDelayEmailPreviewResponse,
} from "./addAdminTypes";

export const adminsApi = adminBaseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAllAdmins: builder.query<GetAllAdminResponse, QueryWithStatus>({
      query: (params) => {
        return {
          url: `admin`,
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getAllHolidays: builder.query<GetAllHolidayResponse, QueryWithStatusYear>({
      query: (params) => {
        return {
          url: `admin/public-holidays`,
          method: "GET",
          params: { ...params },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    addHoliday: builder.mutation<AddHolidayResponse, AddHolidayPayload>({
      query: (payload) => {
        return {
          url: "admin/public-holidays",
          method: "POST",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Holiday added");
        } catch (err) {
          handleError(err);
        }
      },
    }),
    updateHolidayStatus: builder.mutation<
      AddHolidayResponse,
      {
        holidayId: string;
        payload: { status: string };
      }
    >({
      query: ({ holidayId, payload }) => ({
        url: `admin/public-holidays/${holidayId}`,
        method: "PATCH",
        body: payload,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Holiday Confirmed");
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getPayoutDelayEmailPreview: builder.query<
      PayoutDelayEmailPreviewResponse,
      string
    >({
      query: (actionId) => {
        return {
          url: `admin/public-holidays/${actionId}/payout-delay-email-preview`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    sendPayoutDelayEmail: builder.mutation<BaseApiResponse, string>({
      query: (actionId) => {
        return {
          url: `admin/public-holidays/${actionId}/send-payout-delay-email`,
          method: "POST",
        };
      },
    }),
    deleteAdmin: builder.mutation<{ status: string }, string>({
      query: (adminId) => ({
        url: `admin/${adminId}`,
        method: "DELETE",
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Admin deleted");
        } catch (err) {
          handleError(err);
        }
      },
    }),
    changeAdminRole: builder.mutation<
      ChangeAdminRoleResponse,
      {
        adminId: string;
        payload: { role: string };
      }
    >({
      query: ({ adminId, payload }) => ({
        url: `admin/edit-role/${adminId}`,
        method: "PATCH",
        body: payload,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Admin role changed");
        } catch (err) {
          handleError(err);
        }
      },
    }),
  }),
});

export const {
  useGetAllAdminsQuery,
  useGetAllHolidaysQuery,
  useAddHolidayMutation,
  useUpdateHolidayStatusMutation,
  useLazyGetPayoutDelayEmailPreviewQuery,
  useSendPayoutDelayEmailMutation,
  useDeleteAdminMutation,
  useChangeAdminRoleMutation,
} = adminsApi;
